/**
 * BprFrameworkTriangle - the BPR "inverted triangle" renderer.
 *
 * Renders a BprFrameworkContent document as ranked functional-area sections,
 * each holding its ranked issues, and each issue its top solution patterns.
 * Two consumers share it:
 * - the engagement Synthesis view (findings matched onto issues, with a
 *   create-recommendation hook per finding/pattern), and
 * - the framework admin version viewer (citations + confidence per node).
 */

import type {
  BprCitation,
  BprFinding,
  BprFrameworkContent,
  BprIssue,
  BprSolutionPattern,
} from '@protoss/shared';
import type { PillVariant } from '@protoss/ui';
import { Pill } from '@protoss/ui';
import * as React from 'react';

export function bprIssueFindingKey(areaKey: string, issueKey: string): string {
  return `${areaKey}::${issueKey}`;
}

export function bprConfidencePresentation(confidence: number | null | undefined): {
  label: string;
  variant: PillVariant;
} | null {
  if (confidence == null) return null;
  const pct = `${Math.round(confidence * 100)}%`;
  if (confidence >= 0.8) return { label: pct, variant: 'success' };
  if (confidence >= 0.5) return { label: pct, variant: 'warning' };
  return { label: pct, variant: 'danger' };
}

function ConfidenceBadge({ confidence }: { confidence: number | null | undefined }) {
  const presentation = bprConfidencePresentation(confidence);
  if (!presentation) return null;
  return (
    <Pill variant={presentation.variant} className="bpr-triangle__confidence">
      {presentation.label}
    </Pill>
  );
}

function CitationList({ citations }: { citations: BprCitation[] | undefined }) {
  if (!citations || citations.length === 0) return null;
  return (
    <ul className="bpr-triangle__citations">
      {citations.map((citation, idx) => (
        <li key={idx} className="bpr-triangle__citation">
          <span className="bpr-triangle__citation-title">
            {citation.title ?? citation.uri ?? citation.documentUuid ?? 'Source'}
          </span>
          {citation.snippet ? (
            <span className="bpr-triangle__citation-snippet">{citation.snippet}</span>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

export interface BprFrameworkTriangleProps {
  content: BprFrameworkContent;
  /** Findings grouped by bprIssueFindingKey(areaKey, issueKey) (Synthesis view). */
  findingsByIssue?: Map<string, BprFinding[]>;
  /** Show citations under each node (framework admin viewer). */
  showCitations?: boolean;
  /** Create a recommendation prefilled from a pattern (and optionally a finding). */
  onCreateRecommendation?: (args: {
    areaKey: string;
    issueKey: string;
    patternKey?: string;
    findingUuid?: string;
    suggestedTitle?: string;
  }) => void;
  /** Capture a new finding classified onto an issue (Synthesis view). */
  onAddFinding?: (args: { areaKey: string; issueKey: string }) => void;
}

function findingStatusVariant(status: BprFinding['status']): PillVariant {
  switch (status) {
    case 'confirmed':
      return 'success';
    case 'dismissed':
      return 'muted';
    default:
      return 'neutral';
  }
}

function PatternRow({
  areaKey,
  issue,
  pattern,
  showCitations,
  onCreateRecommendation,
}: {
  areaKey: string;
  issue: BprIssue;
  pattern: BprSolutionPattern;
  showCitations: boolean;
  onCreateRecommendation: BprFrameworkTriangleProps['onCreateRecommendation'];
}) {
  return (
    <li className="bpr-triangle__pattern">
      <div className="bpr-triangle__pattern-head">
        <span className="bpr-triangle__pattern-title">{pattern.title}</span>
        <ConfidenceBadge confidence={pattern.confidence} />
        {onCreateRecommendation ? (
          <button
            type="button"
            className="bpr-triangle__link-btn"
            onClick={() =>
              onCreateRecommendation({
                areaKey,
                issueKey: issue.key,
                patternKey: pattern.key,
                suggestedTitle: pattern.title,
              })
            }
          >
            Recommend
          </button>
        ) : null}
      </div>
      {pattern.description ? (
        <p className="bpr-triangle__pattern-description">{pattern.description}</p>
      ) : null}
      {pattern.fitCriteria && pattern.fitCriteria.length > 0 ? (
        <div className="bpr-triangle__pattern-meta">
          <span className="bpr-triangle__pattern-meta-label">Fit:</span>{' '}
          {pattern.fitCriteria.join('; ')}
        </div>
      ) : null}
      {pattern.tradeoffs && pattern.tradeoffs.length > 0 ? (
        <div className="bpr-triangle__pattern-meta">
          <span className="bpr-triangle__pattern-meta-label">Tradeoffs:</span>{' '}
          {pattern.tradeoffs.join('; ')}
        </div>
      ) : null}
      {showCitations ? <CitationList citations={pattern.citations} /> : null}
    </li>
  );
}

export function BprFrameworkTriangle({
  content,
  findingsByIssue,
  showCitations = false,
  onCreateRecommendation,
  onAddFinding,
}: BprFrameworkTriangleProps) {
  const orderedAreas = React.useMemo(
    () => [...content.areas].sort((a, b) => a.rank - b.rank),
    [content.areas]
  );

  return (
    <div className="bpr-triangle">
      {content.summary ? <p className="bpr-triangle__summary">{content.summary}</p> : null}
      <div className="bpr-triangle__areas">
        {orderedAreas.map((area) => {
          const orderedIssues = [...area.issues].sort((a, b) => a.rank - b.rank);
          return (
            <section key={area.key} className="bpr-triangle__area">
              <header className="bpr-triangle__area-head">
                <span className="bpr-triangle__area-rank">{area.rank}</span>
                <h3 className="bpr-triangle__area-title">{area.title}</h3>
                <ConfidenceBadge confidence={area.confidence} />
              </header>
              {area.description ? (
                <p className="bpr-triangle__area-description">{area.description}</p>
              ) : null}
              {showCitations ? <CitationList citations={area.citations} /> : null}

              <div className="bpr-triangle__issues">
                {orderedIssues.map((issue) => {
                  const findings =
                    findingsByIssue?.get(bprIssueFindingKey(area.key, issue.key)) ?? [];
                  return (
                    <article key={issue.key} className="bpr-triangle__issue">
                      <header className="bpr-triangle__issue-head">
                        <span className="bpr-triangle__issue-rank">#{issue.rank}</span>
                        <h4 className="bpr-triangle__issue-title">{issue.title}</h4>
                        <ConfidenceBadge confidence={issue.confidence} />
                        {onAddFinding ? (
                          <button
                            type="button"
                            className="bpr-triangle__link-btn"
                            onClick={() => onAddFinding({ areaKey: area.key, issueKey: issue.key })}
                          >
                            Add finding
                          </button>
                        ) : null}
                      </header>
                      {issue.description ? (
                        <p className="bpr-triangle__issue-description">{issue.description}</p>
                      ) : null}
                      {issue.importance ? (
                        <p className="bpr-triangle__issue-importance">{issue.importance}</p>
                      ) : null}
                      {issue.discoveryVariables && issue.discoveryVariables.length > 0 ? (
                        <div className="bpr-triangle__issue-variables">
                          <span className="bpr-triangle__pattern-meta-label">Discovery:</span>{' '}
                          {issue.discoveryVariables.join('; ')}
                        </div>
                      ) : null}
                      {showCitations ? <CitationList citations={issue.citations} /> : null}

                      {findingsByIssue ? (
                        <div className="bpr-triangle__findings">
                          <span className="bpr-triangle__findings-label">
                            Findings ({findings.length})
                          </span>
                          {findings.length === 0 ? (
                            <p className="bpr-triangle__findings-empty">
                              No findings classified onto this issue yet.
                            </p>
                          ) : (
                            <ul className="bpr-triangle__finding-list">
                              {findings.map((finding) => (
                                <li
                                  key={finding.uuid}
                                  className={`bpr-triangle__finding${
                                    finding.status === 'dismissed'
                                      ? ' bpr-triangle__finding--dismissed'
                                      : ''
                                  }`}
                                >
                                  <div className="bpr-triangle__finding-head">
                                    <Pill variant={findingStatusVariant(finding.status)}>
                                      {finding.status}
                                    </Pill>
                                    <ConfidenceBadge
                                      confidence={finding.classificationConfidence}
                                    />
                                    {onCreateRecommendation ? (
                                      <button
                                        type="button"
                                        className="bpr-triangle__link-btn"
                                        onClick={() =>
                                          onCreateRecommendation({
                                            areaKey: area.key,
                                            issueKey: issue.key,
                                            findingUuid: finding.uuid,
                                            suggestedTitle: issue.title,
                                          })
                                        }
                                      >
                                        Create recommendation
                                      </button>
                                    ) : null}
                                  </div>
                                  {finding.currentStateSummary ? (
                                    <p className="bpr-triangle__finding-summary">
                                      {finding.currentStateSummary}
                                    </p>
                                  ) : null}
                                  {finding.businessVariables &&
                                  finding.businessVariables.length > 0 ? (
                                    <div className="bpr-triangle__finding-variables">
                                      {finding.businessVariables.map((variable) => (
                                        <span
                                          key={variable.key}
                                          className="bpr-triangle__finding-variable"
                                        >
                                          {variable.key}: {variable.value}
                                        </span>
                                      ))}
                                    </div>
                                  ) : null}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : null}

                      {issue.patterns.length > 0 ? (
                        <div className="bpr-triangle__patterns">
                          <span className="bpr-triangle__findings-label">Solution patterns</span>
                          <ul className="bpr-triangle__pattern-list">
                            {issue.patterns.map((pattern) => (
                              <PatternRow
                                key={pattern.key}
                                areaKey={area.key}
                                issue={issue}
                                pattern={pattern}
                                showCitations={showCitations}
                                onCreateRecommendation={onCreateRecommendation}
                              />
                            ))}
                          </ul>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
